<template>
  <b-container>
    <b-card> <canvas ref="roc-chart" width="160" height="90" /> </b-card>
  </b-container>
</template>

<i18n>
{
  "en": {
    "roc_curve": "ROC Curve"
  },
  "fr": {
    "roc_curve": "Courbe ROC"
  }
}
</i18n>

<script>
import _ from 'lodash'
import Chart from 'chart.js'

import chartColors from '@/components/chart_colors.js'
import { get_columnstore_datav2 } from '@/api/subscriptions.js'

import { subscription_mixin } from '@/components/mixin.js'

// *****************************************************************************

class RocChart {
  constructor(el, title) {
    this.el = el
    this.title = title
  }
  init() {
    let self = this
    this.real_chart = new Chart(this.el, {
      type: 'line',
      data: {
        datasets: [],
      },

      options: {
        responsive: true,
        scales: {
          xAxes: [
            {
              type: 'linear',
              position: 'bottom',
            },
          ],
        },
        title: {
          display: true,
          text: this.title,
        },
        tooltips: {
          mode: 'x',
          intersect: false,
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        plugins: {
          streaming: false,
        },
      },
    })
  }
  update(datasets) {
    this.real_chart.data.datasets = datasets
    this.real_chart.update()
  }
}

// *****************************************************************************

export default {
  name: 'RocChart',
  components: {},
  mixins: [subscription_mixin],
  props: ['project_id', 'poid'],
  data() {
    return {
      chart_object: undefined,
      is_mounted: false,
      datasets_options_base: {
        cubicInterpolationMode: 'monotone',
        lineTension: 0,
      },
      chart_datasets: [],
      datasets: [],
    }
  },
  computed: {
    title() {
      return this.$t('roc_curve')
    },
    poid_instantiated() {
      return this.fetch_poid_status('poid_instantiated', this.poid)
    },
  },
  watch: {
    poid_instantiated: {
      immediate: true,
      handler(new_val, old_val) {
        this.build_dataset()
      },
    },
  },
  mounted() {
    this.is_mounted = true
    this.check_chart()
  },
  beforeDestroy() {
    if (this.chart_object) {
      this.chart_object.real_chart.destroy()
    }
  },
  methods: {
    check_chart(reinit = false) {
      if (!this.is_mounted) {
        return
      }
      if (_.isUndefined(this.chart_object) || reinit) {
        if (this.chart_object) {
          this.chart_object.real_chart.destroy()
        }
        this.chart_object = new RocChart(this.$refs['roc-chart'], this.title)
        this.chart_object.init()
        this.chart_datasets = []
      }
      for (let dataset of this.datasets) {
        let existing_dataset = _.find(this.chart_datasets, {
          label: dataset.label,
        })
        if (_.isUndefined(existing_dataset)) {
          this.chart_datasets.push(dataset)
        } else {
          existing_dataset.data = dataset.data
        }
      }
      this.chart_object.update(this.chart_datasets, this.tpr)
    },

    async build_dataset() {
      let tpr = []
      let fpr = []
      let ctpr = []
      if (this.poid_instantiated) {
        let width = 1024
        let payload = await get_columnstore_datav2(
          this.project_id,
          this.poid,
          ['tpr', 'fpr', 'ctpr'],
          null,
          { bins: width }
        )
        // console.log(JSON.stringify(payload))
        tpr = payload.pipe_object[0]
        fpr = payload.pipe_object[1]
        ctpr = payload.pipe_object[2]
        // console.log(tpr)
        // console.log(fpr)
        // console.log(ctpr)
      }
      let datasets = [
        _.merge(
          {
            label: 'ROC',
            backgroundColor: chartColors.blue,
            borderColor: chartColors.blue,
            data: _.map(_.zip(fpr, tpr), elt => {
              return { x: elt[0].toFixed(4), y: elt[1].toFixed(4) }
            }),
            fill: false,
          },
          this.datasets_options_base
        ),
        _.merge(
          {
            label: 'Convex ROC',
            backgroundColor: chartColors.green,
            borderColor: chartColors.green,
            data: _.map(_.zip(fpr, ctpr), elt => {
              return { x: elt[0].toFixed(4), y: elt[1].toFixed(4) }
            }),
            fill: false,
          },
          this.datasets_options_base
        ),
      ]

      this.datasets = datasets
      this.check_chart()
    },
  },
}
</script>
